import {Box, Stack} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {SurveyProgressStyle} from "crqlar-react-common/dist/models/survey/survey.model";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {SurveyProgress} from "../shared/surveyPages.util";
import {surveyContrastColor, surveyControls, surveySolidColors} from "./surveyRunnerColors";

interface Props {
    /** Undefined on the welcome page: the space stays reserved, so nothing jumps when the guest starts. */
    progress?: SurveyProgress
    /** The survey's language: the bar's value text for screen readers is written, and its number formatted, in it. */
    language?: string
    /** The bar's accessible name, "Survey progress" in the survey's language. */
    label?: string
    /** The Design tab's choice: one segment per page (also when unset), or one continuous bar. */
    variant?: SurveyProgressStyle
}

const SEGMENT_HEIGHT = 6;

const SEGMENT_GAP = 4;

const PADDING_TOP = 12;

const PADDING_BOTTOM = 12;

/** Fixed, so the reserved space on the welcome page is exactly as tall as the bar on the first page. */
const PROGRESS_HEIGHT = PADDING_TOP + SEGMENT_HEIGHT + PADDING_BOTTOM;

const formatPercent = (percent: number, language?: string) => {
    try {
        // "35 %" in German, "35%" in English and Italian.
        return new Intl.NumberFormat(language || undefined, {style: 'percent', maximumFractionDigits: 0})
            .format(percent / 100);
    } catch {
        return `${percent}%`;
    }
};

/**
 * One segment per page shown, the current one filling as the guest answers — or, as the Design tab's Bar, one
 * continuous bar. A visual cue only: no visible percentage and no tooltip; screen readers get "x% complete" as the
 * bar's value text. It sits in the frame's sticky header, on the page colour rather than on a card.
 */
function SurveyRunnerProgress({progress, language, label, variant}: Props) {
    const {t} = useTranslation(undefined, {lng: language});
    const theme = useTheme();

    /*
        The header is page, never card, so its text is measured against the page. Where the survey's own colour
        reads there (the usual case) it is used as it is; on a page it would vanish on, the contrast search lifts it.
    */
    const colors = useMemo(() => {
        const page = [surveySolidColors(theme).page];
        return {
            track: alpha(surveyContrastColor(theme.palette.text.primary, page, 4.5), 0.12),
            fill: surveyControls(theme).accent,
        };
    }, [theme]);

    if (!progress) return <Box aria-hidden sx={{height: PROGRESS_HEIGHT}}/>;

    const complete = t('{{percent}} complete', {percent: formatPercent(progress.percent, language)});

    // The bar fills to the value text's own number, so the two never disagree.
    const fills = variant === SurveyProgressStyle.BAR ? [progress.percent / 100] : progress.segments;

    return (
        <Box
            role={'progressbar'}
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress.percent}
            aria-valuetext={complete}
            sx={{height: PROGRESS_HEIGHT, pt: `${PADDING_TOP}px`, boxSizing: 'border-box'}}
        >
            <Stack direction={'row'} gap={`${SEGMENT_GAP}px`}>
                {fills.map((filled, index) => (
                    <Box
                        key={index}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            height: SEGMENT_HEIGHT,
                            borderRadius: `${SEGMENT_HEIGHT / 2}px`,
                            bgcolor: colors.track,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                height: '100%',
                                width: `${filled * 100}%`,
                                borderRadius: 'inherit',
                                bgcolor: colors.fill,
                                transition: 'width 200ms ease-out',
                                '@media (prefers-reduced-motion: reduce)': {transition: 'none'},
                            }}
                        />
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

export default SurveyRunnerProgress;
